import type { Prisma } from '@prisma/client';
import { BusinessCategory, JobCategories } from '@prisma/client';
import { z } from 'zod';

// Named values so they're dev-readable in storage and transit
// Numeric values would mean the order in the enum definition matters (would be fragile in this case)
// Titles of cards are in DashboardCard
export enum DashboardCardType {
	CollectedRevenueChart = 'CollectedRevenueChart',
	ConversionRateChart = 'ConversionRateChart',
	ConversionRateByOwnerChart = 'ConversionRateByOwnerChart',
	DelayedPurchaseOrdersList = 'DelayedPurchaseOrdersList',
	FieldActivityFeed = 'FieldActivityFeed',
	FlaggedJobsList = 'FlaggedJobsList',
	JobAnalyticsPieChart = 'JobAnalyticsPieChart',
	KeyDatesList = 'KeyDatesList',
	NewBidsChart = 'NewBidsChart',
	PastDueCustomerInvoicesList = 'PastDueCustomerInvoicesList',
	SentARInvoicesList = 'SentARInvoicesList',
	PurchaseOrdersArrivingList = 'PurchaseOrdersArrivingList',
	PurchaseOrdersCheckedInList = 'PurchaseOrdersCheckedInList',
	QuotesToFollowUpList = 'QuotesToFollowUpList',
	RecentCompletedInstallsList = 'RecentCompletedInstallsList',
	RecentCompletedMeasuresList = 'RecentCompletedMeasuresList',
	RecentCustomerTextsList = 'RecentCustomerTextsList',
	RecentPaymentsList = 'RecentPaymentsList',
	RejectedPurchaseOrdersList = 'RejectedPurchaseOrdersList',
	UnconfirmedPurchaseOrdersList = 'UnconfirmedPurchaseOrdersList',
	SalesChart = 'SalesChart',
	SpendBySupplierChart = 'SpendBySupplierChart',
	Statistics = 'Statistics',
	WonJobsByCustomerChart = 'WonJobsByCustomerChart',
	WonJobsByOwnerChart = 'WonJobsByOwnerChart',
	WonJobsList = 'WonJobsList',
	RevenueByStage = 'RevenueByStage',
	WatchedJobsList = 'WatchedJobsList',
	UnpaidInvoicesChart = 'UnpaidInvoicesChart',
	AllInvoicesChart = 'AllInvoicesChart',
	InvoicedByJobOwner = 'InvoicedByJobOwner',
	RevenueComparisonChart = 'RevenueComparisonChart',
	RevenueAgent = 'RevenueAgent',
	Playbook = 'Playbook',
}

export const analyticPermissionCards = [
	DashboardCardType.Statistics,
	DashboardCardType.RevenueAgent,

	DashboardCardType.SalesChart,
	DashboardCardType.NewBidsChart,
	DashboardCardType.UnpaidInvoicesChart,
	DashboardCardType.AllInvoicesChart,
	DashboardCardType.InvoicedByJobOwner,
	DashboardCardType.RevenueComparisonChart,
	DashboardCardType.ConversionRateChart,
	DashboardCardType.ConversionRateByOwnerChart,
	DashboardCardType.CollectedRevenueChart,
	DashboardCardType.JobAnalyticsPieChart,
	DashboardCardType.WonJobsByOwnerChart,
	DashboardCardType.WonJobsByCustomerChart,
	DashboardCardType.SpendBySupplierChart,
	DashboardCardType.RevenueByStage,
];

export const nonAnalyticPermissionCards = [
	DashboardCardType.Playbook,
	DashboardCardType.KeyDatesList,
	DashboardCardType.FieldActivityFeed,
	DashboardCardType.FlaggedJobsList,
	DashboardCardType.WonJobsList,
	DashboardCardType.QuotesToFollowUpList,
	DashboardCardType.PurchaseOrdersCheckedInList,
	DashboardCardType.DelayedPurchaseOrdersList,
	DashboardCardType.PurchaseOrdersArrivingList,
	DashboardCardType.RejectedPurchaseOrdersList,
	DashboardCardType.UnconfirmedPurchaseOrdersList,
	DashboardCardType.RecentPaymentsList,
	DashboardCardType.RecentCustomerTextsList,

	DashboardCardType.RecentCompletedMeasuresList,
	DashboardCardType.RecentCompletedInstallsList,
	DashboardCardType.PastDueCustomerInvoicesList,
	DashboardCardType.WatchedJobsList,
	DashboardCardType.SentARInvoicesList,
];

export const defaultDashboardCards = [...analyticPermissionCards, ...nonAnalyticPermissionCards];

const dashboardCardTypes: string[] = Object.values(DashboardCardType);

export const isDashboardCardType = (value: string): value is DashboardCardType => dashboardCardTypes.includes(value);

export const dashboardCardFeatureFlagSelect = {
	enableJobPlaybooks: true,
	enableRevenueAgent: true,
	isCustomerChatEnabled: true,
} satisfies Prisma.BusinessSelect;

export type DashboardCardFeatureFlags = Record<keyof typeof dashboardCardFeatureFlagSelect, boolean>;

export const dashboardCardFeatureFlags: Partial<Record<DashboardCardType, keyof DashboardCardFeatureFlags>> = {
	[DashboardCardType.Playbook]: 'enableJobPlaybooks',
	[DashboardCardType.RecentCustomerTextsList]: 'isCustomerChatEnabled',
	[DashboardCardType.RevenueAgent]: 'enableRevenueAgent',
};

export const isDashboardCardEnabled = (card: DashboardCardType, featureFlags: DashboardCardFeatureFlags) => {
	const featureFlag = dashboardCardFeatureFlags[card];
	return featureFlag === undefined || featureFlags[featureFlag];
};

export const DashboardInputSchema = z.object({
	branchIds: z.array(z.number()),
	// Legacy category filter. New clients send jobCategoryIds; this survives for old
	// bundles and can only address built-in categories.
	businessCategories: z.array(z.enum(BusinessCategory)),
	jobCategoryIds: z.array(z.number().int().positive()).optional(),
	jobCategories: z.array(z.enum(JobCategories)),
	isIncludingJobIds: z.boolean().optional(),
});

export const DashboardInputWithDateRangeSchema = DashboardInputSchema.extend({
	startDate: z.date(),
	endDate: z.date(),
	dateMode: z.enum(['month', 'day']),
});

export type DashboardFilters = z.infer<typeof DashboardInputSchema>;

export type DashboardFiltersWithDateRange = z.infer<typeof DashboardInputWithDateRangeSchema>;

export type DashboardInput = DashboardFilters & {
	businessId: number;
};

export type DashboardInputWithDateRange = DashboardFiltersWithDateRange & {
	businessId: number;
};
